import React, { useState } from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Table, { ColumnsType } from 'antd/lib/table';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import SyncOutlined from '@ant-design/icons/lib/icons/SyncOutlined';
import WarningFilled from '@ant-design/icons/lib/icons/WarningFilled';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { MINUTE_MS } from 'src/domain/commonConst';
import { EMPTY_VALUE } from 'src/components/customer-utility-bill/billStyles';
import { BillStatusTag } from 'src/components/customer-utility-bill/BillStatusTag';
import { useCustomerUtilityBillFilesQuery } from 'src/domain/customer-utility-bill-file/queries';
import {
    ICustomerUtilityBillFile,
    billSourceLabel,
    formatCustomerName,
    isFileReparsable,
    isFileReparseInProgress,
} from 'src/domain/customer-utility-bill-file/interface';
import { IPagination } from 'src/domain/IPagination';

const { Text } = Typography;

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

const allReparsesDone = (rows: ICustomerUtilityBillFile[]): boolean => rows.every(row => !isFileReparseInProgress(row));

const INELIGIBLE_REASON = "Can't be re-parsed in its current status";

interface Row extends ICustomerUtilityBillFile {
    eligible: boolean;
}

interface Props {
    rows: ICustomerUtilityBillFile[];
    // Resolves with the ids the batch endpoint accepted (reparsedIds), used to decide what to track.
    onConfirm: () => Promise<string[]>;
    onClose: () => void;
}

// Same flow as the bills' BillsBatchActionModal (reparse): preview with eligibility warnings, then switch into
// a live-tracking view polling the accepted ids until every one leaves the in-progress statuses.
export const BillFilesReparseModal: React.FC<Props> = ({ rows, onConfirm, onClose }) => {
    // Snapshot once: confirming clears the page's selection, which would otherwise wipe `rows` mid-tracking.
    const [initialRows] = useState(rows);
    const [confirming, setConfirming] = useState(false);
    // null = pre-confirm preview; non-null = tracking the ids the endpoint accepted.
    const [trackedIds, setTrackedIds] = useState<string[] | null>(null);
    const isTracking = trackedIds !== null;

    const { data: page } = useCustomerUtilityBillFilesQuery(
        { ids: (trackedIds ?? []).join(',') },
        {
            enabled: isTracking,
            refetchInterval: (result?: IPagination<ICustomerUtilityBillFile>) =>
                result?.data.length && allReparsesDone(result.data) ? false : MINUTE_MS,
        }
    );
    const liveFiles = page?.data;
    const allDone = isTracking && !!liveFiles?.length && allReparsesDone(liveFiles);

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            const acceptedIds = await onConfirm();
            if (acceptedIds.length > 0) {
                setTrackedIds(acceptedIds);
            } else {
                onClose();
            }
        } finally {
            setConfirming(false);
        }
    };

    const liveById = new Map((liveFiles ?? []).map(file => [file.id, file]));
    // Eligibility is frozen from the pre-confirm snapshot; recomputing from live data would flicker mid-poll.
    const tableRows: Row[] = initialRows.map(row => ({ ...row, ...liveById.get(row.id), eligible: isFileReparsable(row) }));
    const ineligibleCount = tableRows.filter(row => !row.eligible).length;

    const columns: ColumnsType<Row> = [
        {
            title: '',
            dataIndex: 'eligible',
            width: 32,
            render: (eligible: boolean) =>
                !eligible && (
                    <Tooltip title={INELIGIBLE_REASON}>
                        <WarningFilled style={{ color: 'orange' }} />
                    </Tooltip>
                ),
        },
        { title: 'Status', dataIndex: 'processingStatus', render: (_, r) => <BillStatusTag status={r.processingStatus} /> },
        {
            title: 'Error',
            dataIndex: 'processingError',
            render: v =>
                v ? (
                    <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 240 }}>
                        {v}
                    </Text>
                ) : (
                    EMPTY_VALUE
                ),
        },
        { title: 'Source', key: 'source', render: (_, r) => (r.source?.source ? billSourceLabel(r.source.source) : EMPTY_VALUE) },
        {
            title: 'Customer Name',
            key: 'customerName',
            render: (_, r) => (r.source?.customerName ? formatCustomerName(r.source.customerName) : EMPTY_VALUE),
        },
        { title: 'Number of Bills', key: 'numberOfBills', align: 'right', render: (_, r) => r.bills?.length ?? 0 },
        { title: 'Downloaded At', dataIndex: 'downloadedAt', render: v => (v ? getComputerFormattedTime(v) : EMPTY_VALUE) },
        { title: 'Updated At', dataIndex: 'updatedAt', render: v => (v ? getComputerFormattedTime(v) : EMPTY_VALUE) },
    ];

    const trackingMessage = allDone
        ? 'Re-parsing complete.'
        : `Tracking re-parse progress for ${plural(trackedIds?.length ?? 0, 'file')} — refreshing every minute.`;

    return (
        <Modal
            title={`Re-parse ${plural(tableRows.length, 'Customer Utility Bill File')}`}
            open
            width="min(1200px, 95vw)"
            destroyOnHidden
            onCancel={onClose}
            footer={
                isTracking
                    ? [
                          <Button key="close" type="primary" onClick={onClose}>
                              Close
                          </Button>,
                      ]
                    : [
                          <Button key="cancel" onClick={onClose} disabled={confirming}>
                              Cancel
                          </Button>,
                          <Button key="confirm" type="primary" loading={confirming} onClick={handleConfirm}>
                              Re-parse
                          </Button>,
                      ]
            }
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {!isTracking && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Re-parsing deletes all existing bills created from the selected files and creates new ones from the new parse. Manual edits and verification on those bills will be lost."
                    />
                )}
                {isTracking && (
                    <Alert
                        type={allDone ? 'success' : 'info'}
                        showIcon
                        icon={allDone ? undefined : <SyncOutlined spin />}
                        message={trackingMessage}
                    />
                )}
                {ineligibleCount > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${ineligibleCount} of ${tableRows.length} selected files will be skipped: ${INELIGIBLE_REASON.toLowerCase()}.`}
                    />
                )}
                <Table<Row>
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={tableRows}
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                />
            </Space>
        </Modal>
    );
};
